import { useState, FormEvent } from 'react';
import DatePickerField from './DatePickerField';

const submitAddr = 'https://script.google.com/macros/s/AKfycby5T2sih6XEyHVriPZQITmOtawB45aisCR08NdgO7TsOFPV1qzaf4pqPchZ5icCGgHd2w/exec';

export default function RegisterSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [visitDate, setVisitDate] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get('website_alt')?.toString().trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    // Build payload (exclude honeypot)
    const payload = {
      name: formData.get('name')?.toString() || '',
      phone: formData.get('phone')?.toString() || '',
      visit_date: visitDate || '',
      message: formData.get('message')?.toString() || '',
      submitted_at: new Date().toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' }),
    };

    try {
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });

      const responseText = await response.text();

      if (response.ok) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg('등록 중 오류가 발생했습니다. 다시 시도해주세요.');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg('네트워크 오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  return (
    <section id="register" className="relative w-full bg-background-50 overflow-hidden">
      <div className="relative section-padding py-20 md:py-28">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10 md:mb-14">
            <p className="text-xs md:text-sm font-medium text-primary-500 tracking-[0.3em] mb-2">
              REGISTER
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-50">
              관심고객 등록
            </h2>
            <p className="text-sm md:text-base text-foreground-400 mt-4">
              상담을 원하시면 아래 정보를 남겨주세요. 빠르게 연락드리겠습니다.
            </p>
          </div>

          <form
            data-readdy-form
            id="interest-register-form"
            onSubmit={handleSubmit}
            className="p-6 md:p-8 bg-background-100/50 border border-background-200/30 rounded-xl"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="interest-name" className="block text-xs md:text-sm font-medium text-foreground-300 mb-2">
                  이름 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="interest-name"
                  name="name"
                  type="text"
                  required
                  placeholder="성함을 입력해주세요"
                  className="w-full px-4 py-3 bg-background-50/60 border border-background-300/60 rounded-md text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors"
                />
              </div>
              <div>
                <label htmlFor="interest-phone" className="block text-xs md:text-sm font-medium text-foreground-300 mb-2">
                  연락처 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="interest-phone"
                  name="phone"
                  type="tel"
                  required
                  placeholder="010-0000-0000"
                  className="w-full px-4 py-3 bg-background-50/60 border border-background-300/60 rounded-md text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors"
                />
              </div>
            </div>

            <div className="mt-5">
              <label htmlFor="interest-visit-date" className="block text-xs md:text-sm font-medium text-foreground-300 mb-2">
                방문일자
              </label>
              <DatePickerField
                value={visitDate}
                onChange={(e) => setVisitDate(e)}
              />
            </div>

            <div className="mt-5">
              <label htmlFor="interest-message" className="block text-xs md:text-sm font-medium text-foreground-300 mb-2">
                문의내용
              </label>
              <textarea
                id="interest-message"
                name="message"
                rows={4}
                maxLength={500}
                placeholder="문의하실 내용을 입력해주세요 (500자 이내)"
                className="w-full px-4 py-3 bg-background-50/60 border border-background-300/60 rounded-md text-sm text-foreground-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/30 transition-colors resize-none"
              />
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="form-aux-field"
            />

            <div className="mt-7">
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-semibold rounded-full transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                {status === 'submitting' ? '등록 중...' : '등록하기'}
              </button>
            </div>

            {status === 'success' && (
              <p className="mt-4 text-sm text-primary-400 text-center">
                성공적으로 등록되었습니다. 빠르게 연락드리겠습니다.
              </p>
            )}
            {status === 'error' && (
              <p className="mt-4 text-sm text-red-400 text-center">
                {errorMsg}
              </p>
            )}

            <p className="mt-4 text-[11px] text-foreground-400 text-center">
              ※ 등록해주신 정보는 상담 목적으로만 사용됩니다.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}